import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';
import MuiDialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import './styles.scss';
import { useLazyQuery } from '@apollo/client';
import { GET_LOCATION_LIST } from '../../graphql/queries';
import { connect } from 'react-redux';
import ErrorMessage from '../ErrorMessage';
import { DATA_FETCH_ERROR } from '../../common/constants';
import Typography from '@mui/material/Typography';
import SelectDropdown from '../SelectDropdown';
import {
  getDate,
  getRoleDropDownOptions,
  toTitleCase,
} from '../../utils/utils';

const StyledRadioIcon = styled('span')({
  borderRadius: '50%',
  width: 16,
  height: 16,
  boxShadow:
    'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundColor: '#f5f8fa',
  backgroundImage:
    'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  'input:hover ~ &': {
    backgroundColor: '#ebf1f5',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: 'rgba(206,217,224,.5)',
  },
});

const StyledCheckedRadioIcon = styled(StyledRadioIcon)({
  backgroundColor: '#137cbd',
  backgroundImage:
    'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&:before': {
    display: 'block',
    width: 16,
    height: 16,
    backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});

function StyledRadio(props) {
  return (
    <Radio
      sx={{ '&:hover': { backgroundColor: 'transparent' } }}
      disableRipple
      color="default"
      checkedIcon={<StyledCheckedRadioIcon />}
      icon={<StyledRadioIcon />}
      {...props}
    />
  );
}

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

function useMediaQuery(query) {
  const getMatches = (query) => {
    // Prevents SSR issues
    if (typeof globalThis !== 'undefined') {
      return globalThis.matchMedia(query)?.matches ?? false;
    }
    return false;
  };

  const [matches, setMatches] = useState(getMatches(query));

  useEffect(() => {
    const handleChange = () => {
      setMatches(getMatches(query));
    };
    const matchMedia = globalThis.matchMedia(query);
    // Triggered at the first  load and if query changes
    handleChange();
    matchMedia?.addEventListener?.('change', handleChange);
    return () => {
      matchMedia?.removeEventListener?.('change', handleChange);
    };
  }, [query]);

  return matches;
}
const customStyles = {
  option: (provided, state) => ({
    ...provided,
    fontSize: '1.5rem',
    color: state.isSelected ? '#FFFFFF' : '#353535',
    padding: 7,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#0662ed',
  }),
};

const textFieldStyles = {
  placeholder: {
    '& input::placeholder': {
      fontSize: '15px',
    },
    width: '100%',
  },
  placeholderPhone: {
    '& input::placeholder': {
      fontSize: '15px',
      fontWeight: '650',
      fontStyle: 'bold',
    },
    width: '80%',
  },
};

function sortLocations(locations = []) {
  return [...locations].sort((a, b) =>
    (a?.locationName || '')
      .trim()
      .localeCompare((b?.locationName || '').trim(), undefined, {
        sensitivity: 'base',
        numeric: true,
      })
  );
}

export function highlightSearchMatch(text, query) {
  if (!text || !query?.trim()) {
    return text;
  }

  const normalizedQuery = query.trim();
  const escapedQuery = normalizedQuery.replace(
    /[.*+?^${}()|[\]\\]/g,
    String.raw`\$&`
  );
  const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));

  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, index) =>
    part.toLowerCase() === normalizedQuery.toLowerCase() ? (
      <mark key={`${part}-${index}`} className="search-highlight">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function formatLocationNames(locations) {
  return locations.map((location) => {
    const statelocation = location.locationName.split(/\s*[\s,]\s*/).pop();
    let withoutstatelocation;
    if (statelocation.length === 2) {
      withoutstatelocation = location.locationName?.slice(
        0,
        location.locationName.lastIndexOf(',')
      );
    } else {
      withoutstatelocation = location.locationName;
    }
    return {
      ...location,
      cityName: `${withoutstatelocation}`,
      statName: statelocation.length === 2 ? `, ${statelocation}` : '',
    };
  });
}

export const UserImpersonation = ({
  employeeInfo = {},
  impOpen,
  handleImpClose,
}) => {
  const selectedLocation = JSON.parse(
    sessionStorage.getItem('selectedLocation')
  );
  const matches = useMediaQuery('(min-width: 600px)');
  const [tempLocationList, setTempLocationList] = useState([]);
  const [value, setValue] = useState('');
  const [isOpen, setIsOpen] = useState(impOpen);
  const [disableSave, setDisableSave] = useState(true);
  const [location, setLocation] = useState('');
  const userEmail = employeeInfo.adUpn;

  const impIconVisibility = localStorage.getItem('impvisibility');
  let staticUserRole = 'SM';
  if (impIconVisibility?.includes('RD')) {
    staticUserRole = 'RD';
  } else if (impIconVisibility?.includes('DM')) {
    staticUserRole = 'DM';
  }
  const [selectedImpRole, setSelectedImpRole] = React.useState('');
  const [
    getListOfLocation,
    {
      data: { salesSummaryV2: { listOfLocations = [] } = {} } = {},
      error: locationError = {},
      loading: locationLoading,
    },
  ] = useLazyQuery(GET_LOCATION_LIST);

  const sortedLocations = useMemo(
    () => sortLocations(listOfLocations),
    [listOfLocations]
  );

  function callListOfLocations(userEmail, impRole) {
    if (userEmail && impRole) {
      getListOfLocation({
        variables: {
          request: {
            email: userEmail,
            toDate: getDate('YESTERDAY', ''),
            impRole: impRole,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem('jwtToken'),
          },
        },
      });
    }
  }

  const search = (event) => {
    let text = event.target.value.replace(/[^\w\s]/gi, '');
    setValue(text);
    if (text && text !== null && text !== undefined && text.length > 0) {
      setTempLocationList(
        sortedLocations.filter((obj) =>
          (obj?.locationName || '').toUpperCase().includes(text.toUpperCase())
        )
      );
    } else {
      setTempLocationList([]);
      setDisableSave(true);
      setLocation('');
    }
  };

  const handleRadioChange = (event) => {
    setDisableSave(false);
    setLocation(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const newLoc = tempLocationList.find(
      (obj) => obj.locationName === location
    );
    sessionStorage.setItem('selectedLocation', JSON.stringify(newLoc));
    setIsOpen(false);
    globalThis.location.reload();
  };
  const handleOnRoleChange = (option) => {
    setTempLocationList([]);
    setDisableSave(true);
    setLocation('');
    setValue('');
    setSelectedImpRole(option);
    callListOfLocations(userEmail, option.value);
  };

  const callLocationList = formatLocationNames(tempLocationList);

  const customPlaceholder = () => {
    if (!!selectedImpRole && selectedImpRole.value === 'SM') {
      return 'Search Stores';
    } else if (!!selectedImpRole && selectedImpRole.value === 'DM') {
      return 'Search Districts';
    } else if (!!selectedImpRole && selectedImpRole.value === 'RD') {
      return 'Search Regions';
    }
  };
  return (
    <div data-testid="user-impersonation">
      <Dialog
        fullScreen={!matches}
        fullWidth={false}
        height="auto"
        maxWidth="xs"
        aria-labelledby="customized-dialog-title"
        open={isOpen}
        onClose={handleImpClose}
        disableEscapeKeyDown
        className="container"
      >
        <DialogContent>
          {locationError.message ? (
            <ErrorMessage
              statusCode={locationError?.networkError?.statusCode}
              message={DATA_FETCH_ERROR}
            />
          ) : null}
          <div
            data-testid="user-impersonation-container"
            className="user-impersonation-page"
          >
            <Typography component="span" className="title">
              Impersonation
            </Typography>

            <div
              data-testid="user-impersonation-dropdown-container"
              className="dropdown-container"
            >
              {/* <div className="role_title_container">
                <Typography component="span" className="role_title">
                  Role
                </Typography>
              </div> */}
              <div
                data-testid="user-impersonation-dropdown"
                className="impersonate-dropdown-column"
              >
                <SelectDropdown
                  className="impersonate-dropdown-row"
                  styles={customStyles}
                  onChange={handleOnRoleChange}
                  options={getRoleDropDownOptions(staticUserRole)}
                  value={selectedImpRole}
                  isSearchable={false}
                  placeholder={<i>Select Role</i>}
                />
              </div>
            </div>
            {locationLoading && (
              <div className="location-loader">
                <CircularProgress
                  variant="indeterminate"
                  size={30}
                  value={100}
                  color="primary"
                />
              </div>
            )}
            {!locationLoading &&
              !!selectedImpRole &&
              sortedLocations.length > 0 && (
                <div
                  data-testid="user-impersonation-searchbox"
                  className="searchBox-container"
                >
                  {/* <div className="location_title_container">
                    <Typography component="span" className="location_title">
                      Location
                    </Typography>
                  </div> */}
                  <div className="locationSearch-column">
                    <TextField
                      id="outlined-size-small"
                      type="search"
                      variant="outlined"
                      className="search"
                      sx={
                        matches
                          ? textFieldStyles.placeholder
                          : textFieldStyles.placeholderPhone
                      }
                      value={value}
                      size="small"
                      autoComplete="off"
                      onChange={search}
                      placeholder={customPlaceholder()}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment
                              position="start"
                              style={{ width: '10%' }}
                            >
                              <IconButton>
                                <SearchIcon className="search-icon" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </div>
                </div>
              )}
            <div
              data-testid="user-impersonation-location-container"
              className="location-list-container"
            >
              <div className="sample" style={{ width: '10%' }}></div>
              <div className="location-list-column">
                {tempLocationList.length > 0 && !!selectedImpRole ? (
                  <div className="location-list">
                    <RadioGroup
                      name="customized-radios"
                      value={location}
                      onChange={handleRadioChange}
                    >
                      {callLocationList?.map(
                        ({ locationName, cityName, statName }) => (
                          <FormControlLabel
                            disabled={
                              locationName === selectedLocation?.locationName
                            }
                            key={locationName}
                            className="location-row"
                            value={locationName}
                            control={<StyledRadio />}
                            label={highlightSearchMatch(
                              toTitleCase(cityName) + statName,
                              value
                            )}
                          />
                        )
                      )}
                    </RadioGroup>
                  </div>
                ) : (
                  <div className="location-list-blank" />
                )}
              </div>
            </div>
            <div
              data-testid="user-impersonation-button-container"
              className="btn-row"
            >
              <Button
                className="btn MuiButton-textSizeLarge"
                variant="outlined"
                color="primary"
                onClick={handleImpClose}
              >
                Cancel
              </Button>
              <Button
                className="btn MuiButton-textSizeLarge"
                variant="contained"
                color="primary"
                disabled={disableSave}
                onClick={handleSubmit}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

UserImpersonation.propTypes = {
  employeeInfo: PropTypes.shape({
    adUpn: PropTypes.string,
  }),
  impOpen: PropTypes.bool,
  handleImpClose: PropTypes.func,
};

const mapStateToProps = (state) => ({
  employeeInfo: state.auth.employeeInfo,
});

export default connect(mapStateToProps, undefined)(UserImpersonation);
